/**
 * Resolver d'émotion harmonique (review film) + casting figé par exercice.
 */

import { normalizeCadence, DEGREE_TO_FUNCTIONS, FUNCTION_COLORS } from './riemannFunctions'
import {
  getVariantsForEmotionKey,
  emotionAssetUrl,
  BORROWED_BORDER_COLOR,
} from '../data/chordEmotions'

const SPECIAL_ROOT_TO_FUNCTION = {
  N: 'SD',
  It: 'D',
  Fr: 'D',
  Gr: 'D',
}

/**
 * Détermine si le contexte de l'accord est mineur.
 * @param {Object} chord
 * @returns {boolean}
 */
export function isChordMinorContext(chord) {
  if (!chord) return false
  const mode = (chord.degreeMode || '').toString().toLowerCase()
  if (mode === 'minor' || mode === 'mineur') return true
  if (mode === 'major' || mode === 'majeur') return false
  const deg = chord.degree != null ? String(chord.degree).trim() : ''
  if (deg && deg === deg.toLowerCase() && /[ivx]+/i.test(deg)) return true
  return false
}

function normalizeDegreeToken(degree) {
  if (degree == null || degree === '') return null
  return String(degree).trim()
}

function normalizeFigure(figure) {
  if (figure == null || figure === '') return '5'
  const f = String(figure).trim()
  return f || '5'
}

const INVERSION_CAST_FIGURES = new Set(['6', '64', '43', '2'])

/**
 * Normalise les figures de renversement pour le casting (6 ≡ 65).
 * @param {string} fig
 * @returns {string}
 */
function normalizeCastInversionFigure(fig) {
  if (fig === '65') return '6'
  return fig
}

/**
 * Figure harmonique effective (figure ou champ inversion).
 * @param {Object} chord
 * @returns {string}
 */
function getEffectiveFigure(chord) {
  if (chord.inversion != null && String(chord.inversion).trim() !== '') {
    const inv = String(chord.inversion).trim()
    return inv === '42' ? '2' : inv
  }
  return normalizeFigure(chord.figure)
}

/**
 * Suffixe de casting pour différencier les renversements (même pool d'images).
 * Fondamentaux : pas de suffixe. Dominante : V/V7 regroupés ; 6 et 65 partagent :6.
 * @param {Object} chord
 * @returns {string|null} ex. ':6' ou null
 */
export function getCastSuffix(chord) {
  if (!chord) return null
  const fig = normalizeCastInversionFigure(getEffectiveFigure(chord))
  if (!INVERSION_CAST_FIGURES.has(fig)) return null
  return `:${fig}`
}

/**
 * Emprunt / dominante secondaire → clé de casting distincte (même pool).
 * @param {Object} chord
 * @returns {boolean}
 */
export function isEmotionCastBorrowed(chord) {
  if (!chord) return false
  if (chord.isBorrowed) return true
  const from = chord.borrowedFromDegree != null ? String(chord.borrowedFromDegree).trim() : ''
  if (from) return true
  const of = chord.ofDegree != null ? String(chord.ofDegree).trim() : ''
  if (of) return true
  return false
}

function appendCastSuffix(baseEmotionKey, chord) {
  let key = baseEmotionKey
  const inv = getCastSuffix(chord)
  if (inv) key += inv
  if (isEmotionCastBorrowed(chord)) key += ':b'
  return key
}

/**
 * Résout l'identité émotionnelle d'un accord solution (sans choisir le fichier).
 * @param {Object} chord - accord solution
 * @returns {{ emotionKey: string, label: string, functionBorder: string|null, borderColor: string, source: string, isBorrowed: boolean, displayAsFunction: boolean }}
 */
export function resolveChordEmotion(chord) {
  const empty = {
    emotionKey: 'neutral',
    label: 'Neutre',
    functionBorder: null,
    borderColor: 'rgba(255,255,255,0.35)',
    source: 'neutral',
    isBorrowed: false,
    displayAsFunction: false,
  }
  if (!chord) return empty

  const isBorrowed = !!(chord.isBorrowed || chord.specialRoot)
  const isMinor = isChordMinorContext(chord)
  const modeSuffix = isMinor ? 'minor' : 'major'

  const finish = (baseEmotionKey, source, overrides = {}) => {
    let emotionKey = baseEmotionKey
    if (source === 'degree') {
      if (!overrides.skipCastSuffix) {
        emotionKey = appendCastSuffix(baseEmotionKey, chord)
      } else if (isEmotionCastBorrowed(chord)) {
        // Clés spéciales (CAD64, N6…) : pas de suffixe de renversement, mais emprunt distinct
        emotionKey = `${baseEmotionKey}:b`
      }
    }
    const pack = getVariantsForEmotionKey(emotionKey)
    let borderFunction = overrides.functionBorder ?? pack.borderFunction
    if (isBorrowed && !overrides.keepFunctionBorder) {
      // Emprunt : bordure ambre, mais on garde la fonction pédagogique si connue
    }
    const borderColor = isBorrowed
      ? BORROWED_BORDER_COLOR
      : (borderFunction && FUNCTION_COLORS[borderFunction]
        ? FUNCTION_COLORS[borderFunction].primary
        : 'rgba(255,255,255,0.35)')
    return {
      emotionKey,
      label: overrides.label ?? pack.label ?? 'Neutre',
      functionBorder: borderFunction,
      borderColor,
      source,
      isBorrowed,
      displayAsFunction: !!overrides.displayAsFunction,
    }
  }

  // 1. Cadence
  const cadence = normalizeCadence(chord.cadence)
  if (cadence) {
    if (cadence === 'perfect') {
      return finish(`cadence:perfect:${modeSuffix}`, 'cadence', {
        label: 'Entrée / conclusion',
        functionBorder: 'T',
      })
    }
    if (cadence === 'imperfect') {
      return finish(`cadence:imperfect:${modeSuffix}`, 'cadence', {
        label: 'Conclusion partielle',
        functionBorder: 'T',
      })
    }
    if (cadence === 'plagal') {
      return finish('cadence:plagal', 'cadence', {
        label: 'Retour doux',
        functionBorder: 'SD',
      })
    }
    if (cadence === 'rompue') {
      return finish('cadence:rompue', 'cadence', {
        label: 'Surprise',
        functionBorder: 'T',
      })
    }
    if (cadence === 'évitée') {
      return finish('cadence:evitee', 'cadence', {
        label: 'Détournement',
        functionBorder: 'D',
      })
    }
    if (cadence === 'demi-cadence') {
      return finish('cadence:demi-cadence', 'cadence', {
        label: 'Suspens / question',
        functionBorder: 'D',
      })
    }
  }

  // 2. Figures 64
  const degree = normalizeDegreeToken(chord.degree)
  const fig = normalizeFigure(chord.figure)
  const degUpper = degree ? degree.toUpperCase() : null

  if (degUpper === 'I' && fig === '64') {
    if (chord.sixFourVariant === 'cadential') {
      return finish(isMinor ? 'cad64' : 'CAD64', 'degree', {
        label: 'Suspension cadentielle',
        functionBorder: 'D',
        skipCastSuffix: true,
      })
    }
    if (chord.sixFourVariant === 'passing') {
      return finish('V64-passage', 'degree', {
        label: 'Passage',
        functionBorder: 'D',
        skipCastSuffix: true,
      })
    }
    // I64 tonique (avancé)
    return finish('I64-tonique', 'degree', {
      label: 'Suspension tonique',
      functionBorder: 'T',
      skipCastSuffix: true,
    })
  }

  // 3. Spéciaux / pédale
  if (chord.specialRoot === 'N') {
    return finish('N6', 'degree', {
      label: 'Napolitaine',
      functionBorder: 'SD',
      skipCastSuffix: true,
    })
  }
  if (chord.specialRoot === 'It' || chord.specialRoot === 'Fr' || chord.specialRoot === 'Gr') {
    return finish(chord.specialRoot, 'degree', {
      label: `Sixte ${chord.specialRoot}`,
      functionBorder: 'D',
      skipCastSuffix: true,
    })
  }
  if (chord.pedalDegree != null && String(chord.pedalDegree).trim() !== '') {
    // Pédale comme couche visuelle quand c'est le trait dominant sans autre clé plus forte
    // (on arrive ici seulement sans cadence). Toujours Pedal si pedalDegree et pas de degré utile ?
    // Plan : pédale détectée → Pedal. Si on a aussi un degré, le degré prime sauf si on veut marquer la pédale.
    // On réserve Pedal quand il n'y a pas de degré / specialRoot déjà traité.
  }

  // 4. Degré
  if (degree) {
    // vii7
    if (degUpper === 'VII' && (fig === '7' || String(fig).includes('7'))) {
      return finish('vii7', 'degree', { label: 'Tension (vii7)', functionBorder: 'D' })
    }
    if (degUpper === 'VII') {
      return finish('VII', 'degree', { label: 'Tension', functionBorder: 'D' })
    }
    if (degUpper === 'VI') {
      // Majeur → vi (mineur relatif) ; mineur → VI (majeur)
      const mode = (chord.degreeMode || '').toString().toLowerCase()
      const inMinorKey = mode === 'minor' || mode === 'mineur'
      if (inMinorKey) {
        return finish('VI', 'degree', { label: 'Stabilité', functionBorder: 'T' })
      }
      return finish('vi', 'degree', { label: 'Parallèle', functionBorder: 'T' })
    }
    if (degUpper === 'II') {
      return finish(isMinor || degree === degree.toLowerCase() ? 'ii' : 'II', 'degree', {
        label: 'Préparation',
        functionBorder: 'SD',
      })
    }
    if (degUpper === 'III') {
      return finish(isMinor || degree === degree.toLowerCase() ? 'iii' : 'III', 'degree', {
        label: 'Parallèle',
        functionBorder: isMinor ? 'T' : 'T',
      })
    }
    if (degUpper === 'I') {
      return finish(isMinor || degree === 'i' ? 'i' : 'I', 'degree', {
        label: isMinor || degree === 'i' ? 'Repos intérieur' : 'Stabilité',
        functionBorder: 'T',
      })
    }
    if (degUpper === 'IV') {
      return finish(isMinor || degree === 'iv' ? 'sd' : 'SD', 'degree', {
        label: 'Préparation / ouverture',
        functionBorder: 'SD',
      })
    }
    if (degUpper === 'V') {
      // V7 et V → pool D ; dominante mineure v → sd
      if (isMinor || degree === 'v') {
        return finish('d', 'degree', { label: 'Tension / appel', functionBorder: 'D' })
      }
      return finish('D', 'degree', { label: 'Tension / appel', functionBorder: 'D' })
    }
  }

  // Pédale seule (après degré manquant)
  if (chord.pedalDegree != null && String(chord.pedalDegree).trim() !== '' && !degree && !chord.specialRoot) {
    return finish('Pedal', 'degree', { label: 'Pédale', functionBorder: 'SD' })
  }

  // 5. Fonction seule
  const fn =
    chord.selectedFunction ||
    chord.function ||
    (chord.qcmAnswer && ['T', 'SD', 'D'].includes(chord.qcmAnswer) ? chord.qcmAnswer : null)

  if (fn && ['T', 'SD', 'D'].includes(fn)) {
    return finish(`function:${fn}:${modeSuffix}`, 'function', {
      label: fn,
      functionBorder: fn,
      displayAsFunction: true,
    })
  }

  // Fallback fonction depuis degré (si degré connu mais non mappé plus haut)
  if (degUpper) {
    const funcs = DEGREE_TO_FUNCTIONS[degUpper] || []
    if (funcs[0]) {
      return finish(`function:${funcs[0]}:${modeSuffix}`, 'function', {
        label: funcs[0],
        functionBorder: funcs[0],
      })
    }
  }

  if (chord.specialRoot && SPECIAL_ROOT_TO_FUNCTION[chord.specialRoot]) {
    const sf = SPECIAL_ROOT_TO_FUNCTION[chord.specialRoot]
    return finish(`function:${sf}:${modeSuffix}`, 'function', {
      label: sf,
      functionBorder: sf,
    })
  }

  return empty
}

/**
 * Tire un casting figé : emotionKey → nom de fichier.
 * @param {string[]} emotionKeys
 * @param {() => number} [rng] Math.random-like
 * @returns {Record<string, string>}
 */
export function buildEmotionCast(emotionKeys, rng = Math.random) {
  const cast = {}
  const usedFiles = new Set()
  const unique = [...new Set((emotionKeys || []).filter(Boolean))]
  for (const key of unique) {
    const { variants } = getVariantsForEmotionKey(key)
    if (!variants.length) continue
    const available = variants.filter((variant) => !usedFiles.has(variant))
    const pool = available.length > 0 ? available : variants
    const idx = Math.floor(rng() * pool.length)
    const file = pool[Math.max(0, Math.min(pool.length - 1, idx))]
    cast[key] = file
    usedFiles.add(file)
  }
  return cast
}

/**
 * @param {Object} chord
 * @param {Record<string, string>} cast
 * @returns {ReturnType<typeof resolveChordEmotion> & { assetSrc: string|null, assetFile: string|null }}
 */
export function resolveChordEmotionWithCast(chord, cast) {
  const resolved = resolveChordEmotion(chord)
  const file = cast && cast[resolved.emotionKey] ? cast[resolved.emotionKey] : null
  const fallbackPack = getVariantsForEmotionKey(resolved.emotionKey)
  const assetFile = file || (fallbackPack.variants[0] || null)
  return {
    ...resolved,
    assetFile,
    assetSrc: emotionAssetUrl(assetFile),
  }
}

/**
 * Collecte les emotionKeys pour tous les accords solution d'un exercice.
 * @param {Array} correctChords
 */
export function collectEmotionKeysFromSolutions(correctChords) {
  const keys = []
  for (const chord of correctChords || []) {
    if (!chord) continue
    keys.push(resolveChordEmotion(chord).emotionKey)
  }
  return keys
}
